import { CheckIcon, CloseIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons'
import {
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  ModalFooter,
  Button,
  Table,
  Tr,
  Th,
  Tbody,
  Td,
  Link,
  useToast,
  Badge,
  useDisclosure,
  Box,
  Textarea,
  Avatar,
  Flex,
  Spacer,
  IconButton,
  Select,
  Tooltip
} from '@chakra-ui/react'
import React, { useEffect } from 'react'

import { RequestNoteService } from '../../../services/Request/RequestNoteService'
import { RequestService } from '../../../services/Request/RequestService'
import { statuses } from './RequestConsts'
import { RequestDeleteDialog } from './RequestDeleteDialog'
import { RequestNoteDeleteDialog } from './RequestNoteDeleteDialog'
import { RequestStatusLog } from './RequestStatusLog'

interface IProps {
  isOpen: boolean
  onClose: () => void
  refreshRequest: (requestId: number) => Promise<void>
  request: any
}

const ignoredKeys = [
  'bairro',
  'carteira_estudante',
  'cep',
  'cidade',
  'complemento',
  'documento_com_foto',
  'endereco_completo',
  'estado',
  'numero',
  'phone_confirm',
  'reposicao_justificada',
  'schamada_justificada',
  'tipo_asunto_financeiro',
  'your_email',
  'your_email_confirm'
]

export function RequestViewModal({ isOpen, onClose, request, refreshRequest }: IProps) {
  const requestNoteService = new RequestNoteService()
  const { isOpen: deleteDialogIsOpen, onOpen: deleteDialogOnOpen, onClose: deleteDialogIsClose } = useDisclosure()
  const {
    isOpen: deleteNoteDialogIsOpen,
    onOpen: deleteNoteDialogOnOpen,
    onClose: deleteNoteDialogIsClose
  } = useDisclosure()
  const [notes, setNotes] = React.useState<any>([])
  const [note, setNote] = React.useState('')
  const cancelRef = React.useRef()
  const [user, setUser] = React.useState<any>({})
  const [noteToDelete, setNoteToDelete] = React.useState<any>({})
  const [editingConsultant, setEditingConsultant] = React.useState(false)
  const [consultants, setConsultants] = React.useState<string[]>([])
  const [consultant, setConsultant] = React.useState<string>('')

  const requestService = new RequestService()
  const toast = useToast()

  useEffect(() => {
    loadNotes()
    setEditingConsultant(false)
    console.log(request)
  }, [request])

  useEffect(() => {
    const user = localStorage.getItem('user')

    if (user) {
      setUser(JSON.parse(user))
    }

    requestService.getRequestsConsultants().then((response: { consultants: string[] }) => {
      setConsultants(response.consultants)
    })
  }, [])

  const loadNotes = async () => {
    if (!request.id) return

    requestNoteService.list(request.id).then((response) => {
      setNotes(response.notes)
    })
  }

  const handleDelete = async () => {
    deleteDialogOnOpen()
  }

  const handleStatus = async (status: string) => {
    try {
      await requestService.setNewStatus(request.id, status)
      await refreshRequest(request.id)

      toast({
        title: 'Solicitação atualizada com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true
      })
    } catch (error) {
      toast({
        title: 'Erro ao atualizar solicitação',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  const handleSaveNote = async () => {
    if (note === '') return

    try {
      setNote('')
      await requestNoteService.create(request.id, note)
      await loadNotes()
    } catch (error) {
      toast({
        title: 'Erro ao salvar anotação',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  const formatField = (value: string | null) => {
    if (value === null || value === undefined || value === '') {
      return value
    } else if (
      value.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/) ||
      value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{6}Z$/)
    ) {
      return new Date(value).toLocaleDateString() + ' ' + new Date(value).toLocaleTimeString()
    } else if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return new Date(`${value}T00:00:00`).toLocaleDateString()
    }

    return value
  }

  const formatNameField = (value: string) => {
    return value.replace(/_/g, ' ')
  }

  const handleDeleteNote = async (note: any) => {
    setNoteToDelete(note)
    deleteNoteDialogOnOpen()
  }

  const onCloseNoteDelete = () => {
    deleteNoteDialogIsClose()
    loadNotes()
  }

  const handleChangeConsultant = () => {
    setEditingConsultant(false)
    requestService.setNewConsultant(request.id, consultant)
    refreshRequest(request.id)
    toast({
      title: 'Consultor de sucesso atualizado',
      status: 'success',
      duration: 3000,
      isClosable: true
    })
  }

  return (
    request.id && (
      <>
        <ChakraModal isOpen={isOpen} onClose={onClose} size="6xl" isCentered scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              Solicitação de: <strong>{request.your_name}</strong>
              <Badge
                variant="solid"
                colorScheme={statuses[request.status].color}
                size="sm"
                ml={2}
                color={statuses[request.status].textColor}
              >
                {statuses[request.status].text}
              </Badge>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="lg" mb={4} pl={5} borderBottom="1px solid #eee" pb={2} fontWeight="semibold">
                Dados preenchidos:
              </Text>
              <Table>
                <Tbody>
                  {request.tipoDeSolicitacao != null && request.tipoDeSolicitacao != '' && (
                    <Tr>
                      <Th>Tipo de Solicitação</Th>
                      <Td>{formatField(request.tipoDeSolicitacao)}</Td>
                    </Tr>
                  )}
                  {request.data?.tipo_asunto_financeiro != null && request.data.tipo_asunto_financeiro != '' && (
                    <Tr>
                      <Th>Tipo de assunto financeiro</Th>
                      <Td>{formatField(request.data.tipo_asunto_financeiro)}</Td>
                    </Tr>
                  )}
                  <Tr>
                    <Th>Data da solicitação</Th>
                    <Td>
                      {new Date(request.created_at.slice(0, -5)).toLocaleString('pt-BR', {
                        timeZone: 'America/Sao_Paulo'
                      })}
                    </Td>
                  </Tr>
                  <Tr>
                    <Th>Consultor de sucesso</Th>
                    <Td display="flex" alignItems="center">
                      {!editingConsultant && (
                        <>
                          {request.consultor_de_sucesso || 'Não Informado'}
                          <IconButton
                            aria-label="Editar"
                            icon={<EditIcon />}
                            size="sm"
                            ml={2}
                            onClick={() => {
                              setConsultant(request.consultor_de_sucesso)
                              setEditingConsultant(true)
                            }}
                          />
                        </>
                      )}
                      {editingConsultant && (
                        <>
                          <Select
                            value={consultant}
                            onChange={(event) => {
                              setConsultant(event.target.value)
                            }}
                            width="25%"
                          >
                            <option value="Não Informado">Não Informado</option>
                            {consultants.map((consultant) => (
                              <option key={consultant} value={consultant}>
                                {consultant}
                              </option>
                            ))}
                          </Select>
                          <Tooltip label="Salvar" aria-label="Salvar">
                            <IconButton
                              aria-label="Salvar"
                              icon={<CheckIcon />}
                              size="sm"
                              ml={2}
                              onClick={handleChangeConsultant}
                              colorScheme="abraGreen"
                            />
                          </Tooltip>
                          <Tooltip label="Cancelar" aria-label="Cancelar">
                            <IconButton
                              aria-label="Cancelar"
                              icon={<CloseIcon />}
                              size="sm"
                              ml={2}
                              onClick={() => {
                                setEditingConsultant(false)
                              }}
                              colorScheme="red"
                            />
                          </Tooltip>
                        </>
                      )}
                    </Td>
                  </Tr>
                  {request.responsavel_quem != null && request.responsavel_quem != '' && (
                    <Tr>
                      <Th>Tipo de responsável</Th>
                      <Td>{formatField(request.responsavel_quem)}</Td>
                    </Tr>
                  )}
                  {request.nome_responsavel != null && request.nome_responsavel != '' && (
                    <Tr>
                      <Th>Nome do Reponsável</Th>
                      <Td>{formatField(request.nome_responsavel)}</Td>
                    </Tr>
                  )}
                  {request.data?.your_email && request.data?.your_email != '' && (
                    <Tr>
                      <Th>E-mail</Th>
                      <Td>
                        <Link
                          href={`mailto:${request.data.your_email}`}
                          isExternal
                          color="blue.500"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {formatField(request.data.your_email)}
                        </Link>
                      </Td>
                    </Tr>
                  )}
                  {request.phone != null && request.phone != '' && (
                    <Tr>
                      <Th>Telefone</Th>
                      <Td>
                        <Link
                          href={`https://wa.me/55${request.phone.replace(/\D/g, '')}`}
                          isExternal
                          color="blue.500"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {request.phone}
                        </Link>
                      </Td>
                    </Tr>
                  )}
                  {request.formato_curso != null && request.formato_curso != '' && (
                    <Tr>
                      <Th>Formato do Curso</Th>
                      <Td>{formatField(request.formato_curso)}</Td>
                    </Tr>
                  )}
                  {request.curso_presencial != null && request.curso_presencial != '' && (
                    <Tr>
                      <Th>Curso Presencial</Th>
                      <Td>{formatField(request.curso_presencial)}</Td>
                    </Tr>
                  )}
                  {request.curso_online != null && request.curso_online != '' && (
                    <Tr>
                      <Th>Curso Online</Th>
                      <Td>{formatField(request.curso_online)}</Td>
                    </Tr>
                  )}
                  {request.curso_ead_ao_vivo != null && request.curso_ead_ao_vivo != '' && (
                    <Tr>
                      <Th>Curso EAD Ao Vivo</Th>
                      <Td>{formatField(request.curso_ead_ao_vivo)}</Td>
                    </Tr>
                  )}
                  {request.carga_horaria_curso != null && request.carga_horaria_curso != '' && (
                    <Tr>
                      <Th>Carga Horária do Curso</Th>
                      <Td>{formatField(request.carga_horaria_curso)}</Td>
                    </Tr>
                  )}
                  {request.data?.endereco_completo != null && request.data?.endereco_completo != '' && (
                    <Tr>
                      <Th>Endereço completo</Th>
                      <Td>
                        {request.data?.endereco_completo}, {request.data?.numero}, {request.data?.complemento}
                        {request.data?.bairro != null &&
                          request.data?.bairro != '' &&
                          `, ${request.data?.bairro}`} - {request.data?.cidade}/{request.data?.estado} -{' '}
                        {request.data?.cep}
                      </Td>
                    </Tr>
                  )}
                  {Object.keys(request.data).length > 0 &&
                    Object.keys(request.data)
                      .filter((key) => ignoredKeys.indexOf(key) === -1)
                      .map((key: string) => (
                        <Tr key={key}>
                          <Th>{formatNameField(key)}</Th>
                          <Td>{formatField(request.data[key])}</Td>
                        </Tr>
                      ))}
                  {request.data?.carteira_estudante != null && request.data?.carteira_estudante != '' && (
                    <Tr>
                      <Th>Carteira Estudante</Th>
                      <Td>
                        {request.data.carteira_estudante.map(
                          ({ link, filename }: { link: string; filename: string }) => {
                            return (
                              <Link
                                href={link}
                                isExternal
                                color="blue.500"
                                target="_blank"
                                rel="noreferrer"
                                key={filename}
                              >
                                {filename}
                              </Link>
                            )
                          }
                        )}
                      </Td>
                    </Tr>
                  )}
                  {request.data?.documento_com_foto != null && request.data?.documento_com_foto != '' && (
                    <Tr>
                      <Th>Documento Com Foto</Th>
                      <Td>
                        {request.data?.documento_com_foto.map(
                          ({ link, filename }: { link: string; filename: string }) => {
                            return (
                              <Link
                                href={link}
                                isExternal
                                color="blue.500"
                                target="_blank"
                                rel="noreferrer"
                                key={filename}
                                mr={2}
                              >
                                {filename}
                              </Link>
                            )
                          }
                        )}
                      </Td>
                    </Tr>
                  )}
                  {request.data?.reposicao_justificada != null && request.data?.reposicao_justificada != '' && (
                    <Tr>
                      <Th>Reposição justificada</Th>
                      <Td>
                        <Link
                          href={`${request.data?.reposicao_justificada}?nocache=1`}
                          isExternal
                          color="blue.500"
                          target="_blank"
                        >
                          {request.data?.reposicao_justificada}
                        </Link>
                      </Td>
                    </Tr>
                  )}
                  {request.data?.schamada_justificada != null && request.data?.schamada_justificada != '' && (
                    <Tr>
                      <Th>Segunda chamada justificada:</Th>
                      <Td>
                        {request.data?.schamada_justificada.map(
                          ({ link, filename }: { link: string; filename: string }) => {
                            return (
                              <Link
                                href={link}
                                isExternal
                                color="blue.500"
                                target="_blank"
                                rel="noreferrer"
                                key={filename}
                              >
                                {filename}
                              </Link>
                            )
                          }
                        )}
                      </Td>
                    </Tr>
                  )}
                  {request.updated_at != null && request.updated_at != '' && (
                    <Tr>
                      <Th>Última atualização:</Th>
                      <Td>
                        {new Date(request.updated_at.slice(0, -5)).toLocaleString('pt-BR', {
                          timeZone: 'America/Sao_Paulo'
                        })}
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
              <Text fontSize="lg" mb={4} pl={5} borderBottom="1px solid #eee" pb={2} mt={4} fontWeight="semibold">
                Alterações de status:
              </Text>
              <Box pl={5} pr={5}>
                <RequestStatusLog logs={request.change_status_logs} />
              </Box>
              <Text fontSize="lg" mb={4} pl={5} borderBottom="1px solid #eee" pb={2} mt={4} fontWeight="semibold">
                Observações:
              </Text>
              <Box p={5} borderRadius="md" border="2px solid #ff8fbe" mx="auto" boxShadow="md">
                <Flex dir="row" alignItems="center" mb={4}>
                  <Avatar src={`data:image/png;base64,${user.picture}`} mr={3} size="md" />
                  <Text fontWeight="semibold" fontSize="lg">
                    {user.name}
                  </Text>
                </Flex>
                <Textarea
                  border="none"
                  borderRadius="none"
                  placeholder="Digite aqui as observações da solicitação..."
                  borderBottom="1px solid #eee"
                  focusBorderColor="none"
                  rows={4}
                  onChange={(e) => setNote(e.target.value)}
                  value={note}
                />
                <Flex dir="row" justifyContent="flex-end">
                  <Button colorScheme="magenta" mt={4} onClick={handleSaveNote}>
                    Salvar
                  </Button>
                </Flex>
              </Box>
              {notes.length === 0 && (
                <Text fontWeight="semibold" color="gray.400" mt={4} textAlign="center">
                  Nenhuma observação registrada ainda...
                </Text>
              )}
              {notes.map((_note: any) => (
                <Box p={5} borderRadius="md" mt={4} key={_note.id}>
                  <Flex dir="row" alignItems="center" mb={4}>
                    <Avatar src={`data:image/png;base64,${_note.created_by_picture}`} mr={3} h={10} w={10} />
                    <Flex alignItems="baseline">
                      <Text fontWeight="semibold" fontSize="lg">
                        {_note.created_by}
                      </Text>
                      <Text color="gray.500" fontSize="xs" ml={2}>
                        {`${new Date(_note.created_at).toLocaleDateString()} ${new Date(
                          _note.created_at
                        ).toLocaleTimeString()}`}
                      </Text>
                    </Flex>
                    <Spacer />
                    <IconButton
                      aria-label="delete"
                      icon={<DeleteIcon />}
                      colorScheme="red"
                      size="sm"
                      onClick={() => handleDeleteNote(_note)}
                    />
                  </Flex>
                  <Text color="gray.500" mb={4}>
                    {_note.note}
                  </Text>
                  <Flex dir="row" justifyContent="flex-end">
                    <Text color="gray.500" fontSize="sm">
                      {_note.updated_at !== _note.created_at && (
                        <Text as="span" color="gray.500" fontSize="sm">
                          {` - Última edição em ${new Date(_note.updated_at).toLocaleDateString()} ${new Date(
                            _note.updated_at
                          ).toLocaleTimeString()}`}{' '}
                          por <Avatar src={`data:image/png;base64,${_note.updated_by_picture}`} w={5} h={5} />{' '}
                          {_note.updated_by}{' '}
                        </Text>
                      )}
                    </Text>
                  </Flex>
                </Box>
              ))}
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="red" onClick={handleDelete}>
                Excluir
              </Button>
              {(request.status === 'open' || request.status === 'solved') && (
                <Button
                  colorScheme="abraGray"
                  onClick={() => {
                    handleStatus('in_progress')
                  }}
                  ml={4}
                >
                  Marcar como em andamento
                </Button>
              )}
              {request.status === 'in_progress' && (
                <Button
                  colorScheme="abraPurple"
                  onClick={() => {
                    handleStatus('open')
                  }}
                  ml={4}
                >
                  Marcar como em aberto
                </Button>
              )}
              {['Carteira de Estudante ABRA', 'Prova em 2ª Chamada'].includes(request.tipoDeSolicitacao) && (
                <Button
                  colorScheme="abraYellow"
                  color="gray.900"
                  onClick={() => {
                    handleStatus('waiting_payment')
                  }}
                  ml={4}
                >
                  Aguardando Pagamento
                </Button>
              )}
              {request.status === 'in_progress' && (
                <Button
                  colorScheme="abraGreen"
                  onClick={() => {
                    handleStatus('solved')
                  }}
                  ml={4}
                >
                  Marcar como resolvido
                </Button>
              )}
            </ModalFooter>
          </ModalContent>
        </ChakraModal>
        <RequestDeleteDialog
          isOpen={deleteDialogIsOpen}
          onClose={deleteDialogIsClose}
          request={request}
          leastDestructiveRef={cancelRef}
          onDelete={onClose}
        />
        <RequestNoteDeleteDialog
          isOpen={deleteNoteDialogIsOpen}
          onClose={deleteNoteDialogIsClose}
          request={noteToDelete}
          leastDestructiveRef={cancelRef}
          onDelete={onCloseNoteDelete}
        />
      </>
    )
  )
}
